/**
 * Dark-mode hook + a one-time bootstrap on page load.
 *
 * Strategy: simple class-on-<html> toggle. Tailwind already supports
 * `dark:` variants out of the box (configured via media query by default).
 * Since we want USER-driven control, we don't change tailwind config —
 * instead we toggle the `dark` class on the root element ourselves and
 * persist the preference in localStorage so SSR-less reloads remember it.
 *
 * The component-level dark styles can be applied progressively as the
 * design moves — uncovered surfaces still look fine on light, just
 * inverted by the browser/system preference where supported.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "mn_theme";   // "dark" | "light" | "system" (default)

function resolveSystemPref() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(pref) {
  if (typeof document === "undefined") return;
  const effective = pref === "system" ? resolveSystemPref() : pref;
  document.documentElement.classList.toggle("dark", effective === "dark");
  document.documentElement.dataset.theme = effective;
}

/** Call once at app boot so the very first paint already has the right class. */
export function bootstrapTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) || "system";
    applyTheme(saved);
  } catch {
    /* SSR / private mode — silently fall back to light */
  }
}

/** React hook used by toggle UIs. */
export function useDarkMode() {
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "system"; }
    catch { return "system"; }
  });

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); }
    catch { /* ignore */ }
    applyTheme(next);
  }, []);

  // React to system pref changes when in "system" mode.
  useEffect(() => {
    if (theme !== "system" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = () => applyTheme("system");
    mq.addEventListener?.("change", listener);
    return () => mq.removeEventListener?.("change", listener);
  }, [theme]);

  return { theme, setTheme };
}
